import React, { useEffect, useState } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import Drawer from "@mui/material/Drawer";
import { Faders } from "@phosphor-icons/react";
import { FormProvider, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";

import { DynamicField } from "../dynamicField/dynamic-field";
import { FilterDrawerOption, FiltersDrawer as FiltersDrawerType } from "./types";

type Props = {
	filters: FiltersDrawerType;
	setFilters: (filters: FiltersDrawerType | ((prev: FiltersDrawerType) => FiltersDrawerType)) => void;
	filtersOptions: FilterDrawerOption[];
	isFilterDrawerDisabled?: boolean;
};

export function FiltersDrawer({ filters, setFilters, filtersOptions, isFilterDrawerDisabled }: Props) {
	const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
	const [temporaryFilters, setTemporaryFilters] = useState<FiltersDrawerType>(filters);
	const [searchParams, setSearchParams] = useSearchParams();
	const methods = useForm<any>({
		defaultValues: {},
	});

	useEffect(() => {
		if (filtersDrawerOpen) {
			setTemporaryFilters({ ...filters });
			reset();
		}
	}, [filtersDrawerOpen, filters]);

	const { control, reset } = methods;

	const filtersCount = Object.values(filters).filter((filter) => filter.value).length;

	const handleReset = () => {
		const defaultFilters = filtersOptions.reduce((acc, filter) => {
			acc[filter.name] = { ...filter, value: "" };
			return acc;
		}, {} as FiltersDrawerType);

		setFilters(defaultFilters);
		setTemporaryFilters(defaultFilters);

		for (const filter of Object.values(defaultFilters)) {
			if (searchParams.get(filter.name)) {
				searchParams.delete(filter.name);
			}
		}
		setSearchParams(searchParams);

		reset();

		setFiltersDrawerOpen(false);
	};

	const handleApply = () => {
		setFilters({ ...temporaryFilters });

		for (const filter of Object.values(temporaryFilters)) {
			if (searchParams.get(filter.name) && !filter.value) {
				searchParams.delete(filter.name);
			} else if (filter.value) {
				searchParams.set(filter.name, filter.value as string);
			}
		}

		setSearchParams(searchParams);
		setFiltersDrawerOpen(false);
	};

	const handleFilterChange = (name: string, value: any) => {
		setTemporaryFilters({ ...temporaryFilters, [name]: { ...temporaryFilters[name], value } });
	};

	return (
		<>
			<Button
				startIcon={<Faders />}
				sx={{ position: "relative" }}
				variant="outlined"
				color="secondary"
				onClick={() => setFiltersDrawerOpen(true)}
				disabled={isFilterDrawerDisabled}
			>
				Filters
				{filtersCount > 0 && (
					<Typography
						variant="body2"
						sx={{
							position: "absolute",
							right: -8,
							top: -4,
							backgroundColor: "primary.main",
							borderRadius: "50%",
							width: "16px",
							height: "16px",
							color: "white",
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							fontSize: "10px",
						}}
					>
						{filtersCount}
					</Typography>
				)}
			</Button>

			<Drawer
				anchor="right"
				open={filtersDrawerOpen}
				onClose={() => setFiltersDrawerOpen(false)}
				slotProps={{
					backdrop: { invisible: true },
					paper: {
						sx: {
							width: 320,
							backgroundImage: "none",
							backgroundColor: "background.paper",
						},
					},
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						py: 2,
						px: 2.5,
						borderBottom: "1px solid",
						borderColor: "divider",
					}}
				>
					<Typography variant="h6">Filters</Typography>

					<Stack direction="row" spacing={1}>
						{filtersCount > 0 && (
							<Button size="small" variant="outlined" onClick={handleReset} sx={{ fontSize: "0.75rem", px: 1 }}>
								Reset
							</Button>
						)}
						<Button size="small" variant="outlined" onClick={handleApply} sx={{ fontSize: "0.75rem", px: 1 }}>
							Apply
						</Button>
					</Stack>
				</Box>

				<Stack
					sx={{ backgroundColor: "background.paper", py: 3, px: 2.5, gap: 3, overflowY: "auto", flex: "1 1 auto" }}
				>
					<FormProvider {...methods}>
						{filtersOptions.map((filterField) => {
							return (
								<DynamicField
									key={filterField.name}
									control={control}
									name={filterField.name}
									type={filterField.type}
									label={filterField.label}
									dynamicField={{
										...filterField,
										value: filters[filterField.name]?.value as string,
										options: filterField?.options?.options,
										min: filterField?.options?.min,
										max: filterField?.options?.max,
										step: filterField?.options?.step,
									}}
									onChange={(value: any) => handleFilterChange(filterField.name, value)}
								/>
							);
						})}
					</FormProvider>
				</Stack>
			</Drawer>
		</>
	);
}
